import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, CheckCircle2, Sparkles } from "lucide-react";

export default function NewsletterCTA({ source = "home", compact = false }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post("/public/newsletter/subscribe", { email: email.trim().toLowerCase(), source });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Impossible de vous inscrire pour le moment.");
    } finally { setBusy(false); }
  };

  if (compact) {
    return (
      <div className="bg-[#0F0F11] rounded-2xl p-5 text-white" data-testid="newsletter-cta-compact">
        {done ? (
          <div className="flex items-center gap-2 text-[#F2762A]"><CheckCircle2 size={18}/> <span className="text-sm font-semibold">Merci ! Vérifiez votre boîte mail, rendez-vous jeudi 18h.</span></div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[#F2762A] text-xs font-bold uppercase tracking-widest"><Sparkles size={12}/> Newsletter locale</div>
            <div className="mt-2 font-heading font-extrabold text-lg">Les nouveaux bons plans près de chez vous</div>
            <p className="text-xs text-white/70 mt-1">Un email chaque jeudi 18h. Zéro spam.</p>
            <form onSubmit={submit} className="mt-3 flex gap-2">
              <Input type="email" required placeholder="votre@email.fr" value={email} onChange={(e)=>setEmail(e.target.value)}
                     className="bg-white text-slate-900" data-testid="newsletter-email-compact"/>
              <Button type="submit" disabled={busy} size="sm" className="bg-[#E05638] hover:bg-[#c74a2f]" data-testid="newsletter-submit-compact">
                {busy ? "…" : "S'inscrire"}
              </Button>
            </form>
            {err && <div className="mt-2 text-[11px] text-rose-300">{err}</div>}
          </>
        )}
      </div>
    );
  }

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="newsletter-cta">
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-[#E05638] p-8 md:p-12 text-white text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-100 text-xs font-bold uppercase tracking-widest">
          <Mail size={12}/> Newsletter hebdomadaire
        </div>
        <h2 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold">
          Les nouveaux bons plans et actualités locales, dans votre boîte mail
        </h2>
        <p className="mt-3 text-white/85 max-w-xl mx-auto">
          Chaque jeudi 18h : notre sélection des meilleures offres locales, le commerce du mois et les événements à ne pas manquer en Eure-et-Loir.
        </p>
        {done ? (
          <div className="mt-6 inline-flex items-center gap-2 bg-white text-slate-900 rounded-2xl px-5 py-3" data-testid="newsletter-done">
            <CheckCircle2 size={20} className="text-emerald-600"/>
            <span className="font-semibold">C'est fait ! On vous écrit jeudi 18h.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 max-w-md mx-auto flex flex-col sm:flex-row gap-2">
            <Input type="email" required placeholder="votre.email@exemple.fr" value={email} onChange={(e)=>setEmail(e.target.value)}
                   className="bg-white text-slate-900 h-12" data-testid="newsletter-email"/>
            <Button type="submit" disabled={busy} className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-6 font-bold" data-testid="newsletter-submit">
              {busy ? "Envoi…" : "M'inscrire gratuitement"}
            </Button>
          </form>
        )}
        {err && !done && <div className="mt-2 text-sm text-rose-200">{err}</div>}
        <div className="mt-3 text-[11px] text-white/70">Zéro spam · Désinscription en 1 clic · Contenus 100 % locaux</div>
      </div>
    </section>
  );
}
